import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparkleEffect } from '../../components/SparkleEffect';
import { GlitterHeart } from '../../components/GlitterHeart';
import { HeartCrackIcon, DollarSignIcon, SparklesIcon, FlameIcon, StarIcon, Wand2Icon, TrophyIcon, FlagIcon, Dice1Icon, Dice2Icon, Dice3Icon, Dice4Icon, Dice5Icon, Dice6Icon, DicesIcon, CameraIcon, SkullIcon, BriefcaseIcon, GraduationCapIcon, BookOpenIcon, ZapIcon, TvIcon } from 'lucide-react';
// --- TYPES ---
type PlayerColor = 'pink' | 'purple' | 'blue' | 'teal' | 'gold' | 'coral';
interface Job {
  id: string;
  name: string;
  emoji: string;
  wage: number;
  taxType: 'W2' | '1099';
  taxRate: number;
  effectDescription: string;
  flavorText: string;
  tier: 'bad' | 'good';
}
interface Player {
  id: string;
  name: string;
  color: PlayerColor;
  position: number;
  skipNextTurn: boolean;
  avatar: string | null;
  job: Job | null;
  money: number;
  turnsPlayed: number;
  roundsWithoutIncome: number;
  evolved: boolean;
  pathIndex: number | null;
}
type Category = 'start' | 'heartbreak' | 'money' | 'glowup' | 'chaos' | 'blessing' | 'wildcard' | 'finish' | 'tax' | 'money_loss';
interface TileData {
  id: number;
  name: string;
  category: Category;
  actionText: string;
  effect: 'move' | 'skip' | 'roll_again' | 'none' | 'tax' | 'money_loss';
  effectValue?: number;
}
type GamePhase = 'setup' | 'path_choice' | 'job_assignment' | 'playing' | 'tax_event' | 'winner';
// --- JOBS ---
const ALL_JOBS: Job[] = [{
  id: 'broke-streamer',
  name: 'Broke Streamer',
  emoji: '📱',
  wage: 15,
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Roll 1-3: No viewers, skip turn. Roll 4-6: Gift +$50',
  flavorText: 'Going live to 2 people… one is you on another device',
  tier: 'bad'
}, {
  id: 'retail',
  name: 'Retail Worker',
  emoji: '🛍️',
  wage: 15,
  taxType: 'W2',
  taxRate: 0.2,
  effectDescription: 'Customer from hell → go back 2 spaces',
  flavorText: 'The customer is NOT always right',
  tier: 'bad'
}, {
  id: 'fast-food',
  name: 'Fast Food Worker',
  emoji: '🍔',
  wage: 15,
  taxType: 'W2',
  taxRate: 0.2,
  effectDescription: 'Lunch rush meltdown → skip next turn',
  flavorText: "Sir this is a Wendy's",
  tier: 'bad'
}, {
  id: 'gig-driver',
  name: 'Gig Driver',
  emoji: '🚗',
  wage: 18,
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Gas prices hit — lose 50% of round earnings',
  flavorText: 'Made $100… kept $12 after gas and life choices',
  tier: 'bad'
}, {
  id: 'call-center',
  name: 'Call Center Agent',
  emoji: '💻',
  wage: 17,
  taxType: 'W2',
  taxRate: 0.2,
  effectDescription: 'Customer screaming → go back 1 space',
  flavorText: 'Please hold while I hold back tears',
  tier: 'bad'
}, {
  id: 'house-cleaner',
  name: 'House Cleaner',
  emoji: '🧹',
  wage: 18,
  taxType: 'W2',
  taxRate: 0.2,
  effectDescription: '50% chance: +$20 tip OR skip turn',
  flavorText: 'Found something weird under the bed...',
  tier: 'bad'
}, {
  id: 'entrepreneur',
  name: 'Entrepreneur',
  emoji: '🧠',
  wage: 0,
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'No income for 2 rounds. Can evolve after round 5!',
  flavorText: 'You invested in yourself… but forgot bills exist',
  tier: 'bad'
}, {
  id: 'warehouse',
  name: 'Warehouse Worker',
  emoji: '🛒',
  wage: 19,
  taxType: 'W2',
  taxRate: 0.2,
  effectDescription: 'Overworked → skip next turn',
  flavorText: 'My back, my knees, my will to live',
  tier: 'bad'
}, {
  id: 'successful-streamer',
  name: 'Successful Streamer',
  emoji: '🎥',
  wage: 50,
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: '+$100 bonus every 2 rounds',
  flavorText: 'Chat loves you and your bank account agrees',
  tier: 'good'
}, {
  id: 'corporate',
  name: 'Corporate Remote Worker',
  emoji: '💼',
  wage: 35,
  taxType: 'W2',
  taxRate: 0.2,
  effectDescription: '50% chance skip turn (forgot to log in)',
  flavorText: 'Camera off, pants off, productivity off',
  tier: 'good'
}, {
  id: 'healthcare',
  name: 'Healthcare Pro',
  emoji: '🧑‍⚕️',
  wage: 40,
  taxType: 'W2',
  taxRate: 0.2,
  effectDescription: 'Every 3 turns skip 1 (burnout)',
  flavorText: "Saving lives but can't save yourself",
  tier: 'good'
}, {
  id: 'tech',
  name: 'Tech Job',
  emoji: '💻',
  wage: 45,
  taxType: 'W2',
  taxRate: 0.2,
  effectDescription: '+$20 bonus every round (stable)',
  flavorText: 'Debugging code and your life simultaneously',
  tier: 'good'
}];
// --- CONSTANTS ---
const PLAYER_COLORS: Record<PlayerColor, string> = {
  pink: 'bg-pink-500 shadow-pink-500/50',
  purple: 'bg-purple-500 shadow-purple-500/50',
  blue: 'bg-blue-500 shadow-blue-500/50',
  teal: 'bg-teal-500 shadow-teal-500/50',
  gold: 'bg-amber-400 shadow-amber-400/50',
  coral: 'bg-rose-400 shadow-rose-400/50'
};
const PLAYER_BORDER: Record<PlayerColor, string> = {
  pink: 'border-pink-400',
  purple: 'border-purple-400',
  blue: 'border-blue-400',
  teal: 'border-teal-400',
  gold: 'border-amber-300',
  coral: 'border-rose-300'
};
const CATEGORY_STYLES: Record<Category, {
  glow: string;
  border: string;
  bg: string;
  icon: React.ElementType;
}> = {
  start: {
    glow: 'shadow-[0_0_20px_rgba(255,255,255,0.8)]',
    border: 'border-white',
    bg: 'bg-gradient-to-br from-pink-300 via-purple-300 to-blue-300',
    icon: FlagIcon
  },
  heartbreak: {
    glow: 'shadow-[0_0_15px_rgba(244,63,94,0.4)]',
    border: 'border-rose-300',
    bg: 'bg-rose-50',
    icon: HeartCrackIcon
  },
  money: {
    glow: 'shadow-[0_0_15px_rgba(251,191,36,0.4)]',
    border: 'border-amber-300',
    bg: 'bg-amber-50',
    icon: DollarSignIcon
  },
  glowup: {
    glow: 'shadow-[0_0_15px_rgba(168,85,247,0.4)]',
    border: 'border-purple-300',
    bg: 'bg-purple-50',
    icon: SparklesIcon
  },
  chaos: {
    glow: 'shadow-[0_0_15px_rgba(249,115,22,0.4)]',
    border: 'border-orange-300',
    bg: 'bg-orange-50',
    icon: FlameIcon
  },
  blessing: {
    glow: 'shadow-[0_0_15px_rgba(20,184,166,0.4)]',
    border: 'border-teal-300',
    bg: 'bg-teal-50',
    icon: StarIcon
  },
  wildcard: {
    glow: 'shadow-[0_0_20px_rgba(236,72,153,0.5)]',
    border: 'border-pink-300',
    bg: 'bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50',
    icon: Wand2Icon
  },
  finish: {
    glow: 'shadow-[0_0_30px_rgba(251,191,36,0.6)]',
    border: 'border-amber-300',
    bg: 'bg-gradient-to-br from-amber-100 via-yellow-200 to-amber-300',
    icon: TrophyIcon
  },
  tax: {
    glow: 'shadow-[0_0_20px_rgba(220,38,38,0.5)]',
    border: 'border-red-400',
    bg: 'bg-gradient-to-br from-red-50 via-red-50 to-orange-50',
    icon: SkullIcon
  },
  money_loss: {
    glow: 'shadow-[0_0_18px_rgba(239,68,68,0.45)]',
    border: 'border-red-300',
    bg: 'bg-gradient-to-br from-red-50 via-rose-50 to-pink-50',
    icon: DollarSignIcon
  }
};
// --- TILES (30 tiles) ---
const TILES: TileData[] = [{
  id: 0,
  name: 'The Journey Begins',
  category: 'start',
  actionText: 'Ready to trip through life?',
  effect: 'none'
}, {
  id: 1,
  name: 'Your Ex Texted',
  category: 'heartbreak',
  actionText: 'Take a shot or skip a turn',
  effect: 'skip'
}, {
  id: 2,
  name: 'Side Hustle Paid Off',
  category: 'money',
  actionText: 'Move ahead 2 spaces',
  effect: 'move',
  effectValue: 2
}, {
  id: 3,
  name: 'Car Broke Down',
  category: 'chaos',
  actionText: 'Move back 1 space',
  effect: 'move',
  effectValue: -1
}, {
  id: 4,
  name: 'New Haircut, New You',
  category: 'glowup',
  actionText: 'Roll again!',
  effect: 'roll_again'
}, {
  id: 5,
  name: 'Found $20 in Old Jeans',
  category: 'blessing',
  actionText: 'Move ahead 1 space',
  effect: 'move',
  effectValue: 1
}, {
  id: 6,
  name: 'Got Ghosted Again',
  category: 'heartbreak',
  actionText: 'Skip next turn',
  effect: 'skip'
}, {
  id: 7,
  name: 'WILD CARD',
  category: 'wildcard',
  actionText: 'Host picks your fate',
  effect: 'none'
}, {
  id: 8,
  name: 'Rent Due, Account Empty',
  category: 'money',
  actionText: 'Move back 2 spaces',
  effect: 'move',
  effectValue: -2
}, {
  id: 9,
  name: 'Started Therapy',
  category: 'glowup',
  actionText: 'Move ahead 3 spaces',
  effect: 'move',
  effectValue: 3
}, {
  id: 10,
  name: '💸 TAX SEASON',
  category: 'tax',
  actionText: 'The IRS has entered the chat. ALL players get taxed!',
  effect: 'tax'
}, {
  id: 11,
  name: 'Stranger Paid for Coffee',
  category: 'blessing',
  actionText: 'Move ahead 1 space',
  effect: 'move',
  effectValue: 1
}, {
  id: 12,
  name: 'Caught Them Cheating',
  category: 'heartbreak',
  actionText: 'Move back 3 spaces',
  effect: 'move',
  effectValue: -3
}, {
  id: 13,
  name: 'Got a Raise!',
  category: 'money',
  actionText: 'Move ahead 2 spaces',
  effect: 'move',
  effectValue: 2
}, {
  id: 14,
  name: 'WILD CARD',
  category: 'wildcard',
  actionText: 'Swap places with any player',
  effect: 'none'
}, {
  id: 15,
  name: 'Gym Glow Up Era',
  category: 'glowup',
  actionText: 'Roll again!',
  effect: 'roll_again'
}, {
  id: 16,
  name: 'Identity Stolen',
  category: 'chaos',
  actionText: 'Move back 2 spaces',
  effect: 'move',
  effectValue: -2
}, {
  id: 17,
  name: 'Dream Job Called Back',
  category: 'blessing',
  actionText: 'Move ahead 3 spaces',
  effect: 'move',
  effectValue: 3
}, {
  id: 18,
  name: 'Toxic Friend Exposed',
  category: 'heartbreak',
  actionText: 'Skip a turn',
  effect: 'skip'
}, {
  id: 19,
  name: 'Crypto Crashed',
  category: 'money',
  actionText: 'Move back 1 space',
  effect: 'move',
  effectValue: -1
}, {
  id: 20,
  name: '💸 TAX SEASON',
  category: 'tax',
  actionText: 'Uncle Sam wants his cut. ALL players get taxed!',
  effect: 'tax'
}, {
  id: 21,
  name: 'WILD CARD',
  category: 'wildcard',
  actionText: 'Everyone moves back 1 except you',
  effect: 'none'
}, {
  id: 22,
  name: 'Apartment Flooded',
  category: 'chaos',
  actionText: 'Move back 2 spaces',
  effect: 'move',
  effectValue: -2
}, {
  id: 23,
  name: 'Met Your Person',
  category: 'blessing',
  actionText: 'Move ahead 3 spaces',
  effect: 'move',
  effectValue: 3
}, {
  id: 24,
  name: 'Wedding Called Off',
  category: 'heartbreak',
  actionText: 'Move back 3 spaces',
  effect: 'move',
  effectValue: -3
}, {
  id: 25,
  name: 'Tax Refund Hit!',
  category: 'money',
  actionText: 'Move ahead 2 spaces',
  effect: 'move',
  effectValue: 2
}, {
  id: 26,
  name: 'Wrote Your First Book',
  category: 'glowup',
  actionText: 'Move ahead 2 spaces',
  effect: 'move',
  effectValue: 2
}, {
  id: 27,
  name: 'Lost Your Keys... Again',
  category: 'chaos',
  actionText: 'Skip a turn',
  effect: 'skip'
}, {
  id: 28,
  name: '💸 TAX SEASON',
  category: 'tax',
  actionText: 'Final tax hit before the finish! ALL players get taxed!',
  effect: 'tax'
}, {
  id: 29,
  name: 'You Made It, Babe!',
  category: 'finish',
  actionText: 'Winner winner!',
  effect: 'none'
},
// --- 💸 UNIVERSAL MONEY STRUGGLE TILES ---
// Bills / Adulting Pain
{
  id: 30,
  name: 'Rent Went Up 😬',
  category: 'money_loss',
  actionText: 'Rent went up this month...',
  effect: 'money_loss',
  effectValue: -150
}, {
  id: 31,
  name: 'Utility Bill Shock ⚡',
  category: 'money_loss',
  actionText: 'Utility bill way higher than expected',
  effect: 'money_loss',
  effectValue: -100
}, {
  id: 32,
  name: 'Internet Bill Up',
  category: 'money_loss',
  actionText: 'Internet bill randomly increased',
  effect: 'money_loss',
  effectValue: -20
}, {
  id: 33,
  name: 'Forgot a Subscription 💸',
  category: 'money_loss',
  actionText: 'Subscription you forgot about hit again',
  effect: 'money_loss',
  effectValue: -25
}, {
  id: 34,
  name: 'Late Fee 😒',
  category: 'money_loss',
  actionText: 'Late fee added to your bill',
  effect: 'money_loss',
  effectValue: -35
},
// Life Expenses
{
  id: 35,
  name: 'Gas Prices Spike ⛽',
  category: 'money_loss',
  actionText: 'Gas prices are insane right now',
  effect: 'money_loss',
  effectValue: -50
}, {
  id: 36,
  name: 'Car Maintenance 🛠️',
  category: 'money_loss',
  actionText: 'Car needs maintenance... again',
  effect: 'money_loss',
  effectValue: -150
}, {
  id: 37,
  name: 'Flat Tire 😭',
  category: 'money_loss',
  actionText: "Flat tire… and you're already late for work",
  effect: 'money_loss',
  effectValue: -100
}, {
  id: 38,
  name: 'Speeding Ticket 🚓',
  category: 'money_loss',
  actionText: 'Caught going 15 over...',
  effect: 'money_loss',
  effectValue: -275
}, {
  id: 39,
  name: 'Car Got Towed 😭',
  category: 'money_loss',
  actionText: 'Towed… pay to get it back',
  effect: 'money_loss',
  effectValue: -350
},
// Health / Real Life
{
  id: 40,
  name: 'Doctor Visit 🏥',
  category: 'money_loss',
  actionText: 'Doctor visit… not covered by insurance',
  effect: 'money_loss',
  effectValue: -700
}, {
  id: 41,
  name: 'Prescription Cost 💊',
  category: 'money_loss',
  actionText: 'Prescription cost hits different',
  effect: 'money_loss',
  effectValue: -300
}, {
  id: 42,
  name: 'Dental Expense 😬',
  category: 'money_loss',
  actionText: 'Unexpected dental expense',
  effect: 'money_loss',
  effectValue: -500
},
// Bad Decisions
{
  id: 43,
  name: 'Retail Therapy 🛍️',
  category: 'money_loss',
  actionText: 'Retail therapy got you again',
  effect: 'money_loss',
  effectValue: -450
}, {
  id: 44,
  name: 'Impulse Buy 😅',
  category: 'money_loss',
  actionText: 'Impulse buy regret is real',
  effect: 'money_loss',
  effectValue: -200
}, {
  id: 45,
  name: 'Ordered Food Again 🍔',
  category: 'money_loss',
  actionText: 'Ordered food instead of cooking',
  effect: 'money_loss',
  effectValue: -20
}, {
  id: 46,
  name: 'Boredom Spending',
  category: 'money_loss',
  actionText: 'Spent money out of pure boredom',
  effect: 'money_loss',
  effectValue: -25
}, {
  id: 47,
  name: 'TikTok Made Me Buy It 😭',
  category: 'money_loss',
  actionText: 'TikTok made you buy it...',
  effect: 'money_loss',
  effectValue: -60
},
// Bank / Money System Pain
{
  id: 48,
  name: 'Credit Card Interest 😩',
  category: 'money_loss',
  actionText: 'Credit card interest hit hard',
  effect: 'money_loss',
  effectValue: -28
}, {
  id: 49,
  name: 'Mystery Bank Fee',
  category: 'money_loss',
  actionText: "Bank fee you didn't understand",
  effect: 'money_loss',
  effectValue: -30
}, {
  id: 50,
  name: 'Double Charged 💀',
  category: 'money_loss',
  actionText: 'Payment processed twice!',
  effect: 'money_loss',
  effectValue: -150
}, {
  id: 51,
  name: 'Low Balance Fee 😒',
  category: 'money_loss',
  actionText: 'Low balance fee hit again',
  effect: 'money_loss',
  effectValue: -35
},
// Low-Key Stress Money Hits
{
  id: 52,
  name: 'Helped Someone Out 💔',
  category: 'money_loss',
  actionText: 'Gave money to someone who needed it',
  effect: 'money_loss',
  effectValue: -5
}, {
  id: 53,
  name: 'Bought a Meal ❤️',
  category: 'money_loss',
  actionText: 'Bought someone a meal',
  effect: 'money_loss',
  effectValue: -12
}, {
  id: 54,
  name: 'TikTok Gifts 😭',
  category: 'money_loss',
  actionText: 'Threw too many TikTok gifts',
  effect: 'money_loss',
  effectValue: -100
},
// Extra scattered hits
{
  id: 55,
  name: 'Parking Ticket 🅿️',
  category: 'money_loss',
  actionText: 'Forgot to feed the meter',
  effect: 'money_loss',
  effectValue: -75
}, {
  id: 56,
  name: 'Phone Screen Cracked 📱',
  category: 'money_loss',
  actionText: 'Dropped your phone face down...',
  effect: 'money_loss',
  effectValue: -200
}, {
  id: 57,
  name: 'Pet Vet Bill 🐾',
  category: 'money_loss',
  actionText: 'Your fur baby needed the vet',
  effect: 'money_loss',
  effectValue: -400
}];
// --- 4 LIFE PATHS ---
const LIFE_PATHS = [{
  name: 'College → Best Job',
  emoji: '🎓',
  icon: GraduationCapIcon,
  color: 'from-violet-400 to-purple-500',
  glowColor: 'rgba(139,92,246,0.5)',
  textColor: 'text-violet-600',
  bgLight: 'bg-violet-50',
  borderColor: 'border-violet-300'
}, {
  name: 'HS / GED → Good Job',
  emoji: '📚',
  icon: BookOpenIcon,
  color: 'from-pink-400 to-rose-500',
  glowColor: 'rgba(236,72,153,0.5)',
  textColor: 'text-pink-600',
  bgLight: 'bg-pink-50',
  borderColor: 'border-pink-300'
}, {
  name: 'Drop Out → Min Wage',
  emoji: '⚡',
  icon: ZapIcon,
  color: 'from-orange-400 to-coral-500',
  glowColor: 'rgba(251,146,60,0.5)',
  textColor: 'text-orange-600',
  bgLight: 'bg-orange-50',
  borderColor: 'border-orange-300'
}, {
  name: 'Self Employed → Streaming',
  emoji: '📺',
  icon: TvIcon,
  color: 'from-teal-400 to-cyan-500',
  glowColor: 'rgba(20,184,166,0.5)',
  textColor: 'text-teal-600',
  bgLight: 'bg-teal-50',
  borderColor: 'border-teal-300'
}];
// --- PATH-SPECIFIC JOB POOLS ---
const PATH_JOBS: Record<number, string[]> = {
  0: ['corporate', 'healthcare', 'tech'],
  1: ['call-center', 'warehouse', 'retail'],
  2: ['fast-food', 'gig-driver', 'house-cleaner'],
  3: ['broke-streamer', 'entrepreneur'] // Self Employed → Streaming
};
const getJobsForPath = (pathIndex: number): Job[] => {
  const jobIds = PATH_JOBS[pathIndex] || [];
  return ALL_JOBS.filter(j => jobIds.includes(j.id));
};
// Map tiles to 4 paths — original tiles + money struggle tiles scattered in each
// Money struggle tiles (30-57) are distributed evenly and mixed into early/mid/late positions
const PATH_TILE_ASSIGNMENTS = [
// Path 0: College → Best Job (7 original + 7 money struggles)
[1, 30, 2, 35, 3, 40, 4, 48, 5, 44, 6, 52, 7, 55],
// Path 1: HS/GED → Good Job (7 original + 7 money struggles)
[8, 31, 9, 36, 11, 41, 12, 49, 13, 45, 14, 53, 15, 56],
// Path 2: Drop Out → Min Wage (7 original + 7 money struggles)
[16, 32, 17, 37, 18, 42, 19, 50, 21, 46, 22, 54, 23, 57],
// Path 3: Self Employed → Streaming (7 original + 7 money struggles)
[24, 33, 25, 38, 26, 43, 27, 51, 34, 47, 39, 28, 29, 29]];
// Position calculator for the 4-path layout
// Center of board at (50%, 50%), paths radiate outward
const getBoardPosition = (tileId: number): {
  x: number;
  y: number;
} => {
  // Start tile — center
  if (tileId === 0) return {
    x: 50,
    y: 50
  };
  // Finish tile — top center
  if (tileId === 29) return {
    x: 50,
    y: 4
  };
  // Tax tiles — positioned along the paths
  if (tileId === 10) return {
    x: 25,
    y: 32
  };
  if (tileId === 20) return {
    x: 75,
    y: 32
  };
  if (tileId === 28) return {
    x: 50,
    y: 14
  };
  // Find which path this tile belongs to
  for (let pathIdx = 0; pathIdx < PATH_TILE_ASSIGNMENTS.length; pathIdx++) {
    const pathTiles = PATH_TILE_ASSIGNMENTS[pathIdx];
    const posInPath = pathTiles.indexOf(tileId);
    if (posInPath !== -1) {
      const progress = (posInPath + 1) / (pathTiles.length + 1);
      // 4 paths: top-left, top-right, bottom-left, bottom-right curving to top
      const angles = [-135, -45, 135, 45]; // degrees from center
      const angle = angles[pathIdx] * Math.PI / 180;
      // Wider spread for more tiles, with slight S-curve
      const radius = 12 + progress * 35;
      // Curve paths upward toward finish more aggressively
      const curveUp = progress * progress * 25;
      // Add slight wobble to prevent overlap
      const wobble = Math.sin(posInPath * 1.2) * 3;
      const x = 50 + Math.cos(angle) * radius + wobble;
      const y = 50 + Math.sin(angle) * radius - curveUp;
      return {
        x: Math.max(4, Math.min(96, x)),
        y: Math.max(4, Math.min(96, y))
      };
    }
  }
  // Fallback for any unmapped tiles
  const row = Math.floor(tileId / 6);
  const col = tileId % 6;
  return {
    x: 10 + col * 16,
    y: 10 + row * 18
  };
};
// Floating particles component
function FloatingParticles({
  count = 30
}: {
  count?: number;
}) {
  return <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" data-id="element-1657">
      {Array.from({
      length: count
    }).map((_, i) => <motion.div key={i} className="absolute rounded-full" style={{
      width: 2 + Math.random() * 4,
      height: 2 + Math.random() * 4,
      background: ['rgba(236,72,153,0.3)', 'rgba(168,85,247,0.3)', 'rgba(96,165,250,0.3)', 'rgba(251,191,36,0.3)', 'rgba(255,255,255,0.5)'][i % 5],
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`
    }} animate={{
      y: [0, -30 - Math.random() * 40, 0],
      x: [0, (Math.random() - 0.5) * 20, 0],
      opacity: [0.2, 0.6, 0.2],
      scale: [1, 1.3, 1]
    }} transition={{
      duration: 4 + Math.random() * 6,
      repeat: Infinity,
      delay: Math.random() * 5,
      ease: 'easeInOut'
    }} data-id="element-1658" />)}
    </div>;
}
// Sparkle burst component for board landing
function SparkleBurst({
  active
}: {
  active: boolean;
}) {
  if (!active) return null;
  return <AnimatePresence data-id="element-1659">
      <div className="absolute inset-0 pointer-events-none z-10" data-id="element-1660">
        {Array.from({
        length: 40
      }).map((_, i) => {
        const angle = i / 40 * Math.PI * 2;
        const distance = 80 + Math.random() * 120;
        return <motion.div key={i} className="absolute left-1/2 top-1/2 rounded-full" style={{
          width: 3 + Math.random() * 5,
          height: 3 + Math.random() * 5,
          background: ['#f9a8d4', '#c4b5fd', '#93c5fd', '#fcd34d', '#ffffff'][i % 5]
        }} initial={{
          x: 0,
          y: 0,
          opacity: 1,
          scale: 1
        }} animate={{
          x: Math.cos(angle) * distance,
          y: Math.sin(angle) * distance - 40,
          opacity: 0,
          scale: 0
        }} transition={{
          duration: 1.2 + Math.random() * 0.8,
          ease: 'easeOut'
        }} data-id="element-1661" />;
      })}
      </div>
    </AnimatePresence>;
}
// --- COMPONENT ---
export function BoardGamePage() {
  const [phase, setPhase] = useState<GamePhase>('setup');
  const [players, setPlayers] = useState<Player[]>([{
    id: '1',
    name: 'Player 1',
    color: 'pink',
    position: 0,
    skipNextTurn: false,
    avatar: null,
    job: null,
    money: 0,
    turnsPlayed: 0,
    roundsWithoutIncome: 0,
    evolved: false,
    pathIndex: null
  }, {
    id: '2',
    name: 'Player 2',
    color: 'purple',
    position: 0,
    skipNextTurn: false,
    avatar: null,
    job: null,
    money: 0,
    turnsPlayed: 0,
    roundsWithoutIncome: 0,
    evolved: false,
    pathIndex: null
  }]);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [diceValue, setDiceValue] = useState<number | null>(null);
  const [isRolling, setIsRolling] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [winner, setWinner] = useState<Player | null>(null);
  const [zoomedTile, setZoomedTile] = useState<number | null>(null);
  const [jobRevealIndex, setJobRevealIndex] = useState(-1);
  const [jobSpinning, setJobSpinning] = useState(false);
  const [taxResults, setTaxResults] = useState<Array<{
    name: string;
    emoji: string;
    taxType: string;
    taxed: number;
    remaining: number;
  }>>([]);
  const [showEvolution, setShowEvolution] = useState(false);
  const [evolvedJob, setEvolvedJob] = useState<Job | null>(null);
  const [jobEffectMessage, setJobEffectMessage] = useState<string | null>(null);
  const [incomeFlash, setIncomeFlash] = useState<string | null>(null);
  const [boardLanded, setBoardLanded] = useState(false);
  const [showBurst, setShowBurst] = useState(false);
  const [glowingPath, setGlowingPath] = useState(0);
  const boardRef = useRef<HTMLDivElement>(null);
  const [pathChoicePlayerIndex, setPathChoicePlayerIndex] = useState(0);
  // Cycle path glow
  useEffect(() => {
    if (phase !== 'playing') return;
    const interval = setInterval(() => {
      setGlowingPath(prev => (prev + 1) % 4);
    }, 2500);
    return () => clearInterval(interval);
  }, [phase]);
  // Board landing animation trigger
  useEffect(() => {
    if (phase === 'playing' && !boardLanded) {
      setTimeout(() => {
        setBoardLanded(true);
        setShowBurst(true);
        setTimeout(() => setShowBurst(false), 2000);
      }, 300);
    }
  }, [phase]);
  // --- SETUP ---
  const addPlayer = () => {
    if (players.length >= 6) return;
    const colors: PlayerColor[] = ['pink', 'purple', 'blue', 'teal', 'gold', 'coral'];
    setPlayers([...players, {
      id: Date.now().toString(),
      name: `Player ${players.length + 1}`,
      color: colors[players.length % 6],
      position: 0,
      skipNextTurn: false,
      avatar: null,
      job: null,
      money: 0,
      turnsPlayed: 0,
      roundsWithoutIncome: 0,
      evolved: false,
      pathIndex: null
    }]);
  };
  const removePlayer = (id: string) => {
    if (players.length <= 2) return;
    setPlayers(players.filter(p => p.id !== id));
  };
  const updatePlayer = (id: string, field: keyof Player, value: any) => {
    setPlayers(players.map(p => p.id === id ? {
      ...p,
      [field]: value
    } : p));
  };
  const handleAvatarUpload = (playerId: string, file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      setPlayers(prev => prev.map(p => p.id === playerId ? {
        ...p,
        avatar: e.target?.result as string
      } : p));
    };
    reader.readAsDataURL(file);
  };
  const goToPathChoice = () => {
    setPlayers(players.map(p => ({
      ...p,
      position: 0,
      skipNextTurn: false,
      money: 0,
      turnsPlayed: 0,
      roundsWithoutIncome: 0,
      job: null,
      evolved: false,
      pathIndex: null
    })));
    setPhase('path_choice');
    setPathChoicePlayerIndex(0);
  };
  const selectPath = (playerIndex: number, pathIndex: number) => {
    setPlayers(prev => prev.map((p, i) => i === playerIndex ? {
      ...p,
      pathIndex
    } : p));
    if (playerIndex + 1 < players.length) {
      setPathChoicePlayerIndex(playerIndex + 1);
    } else {
      goToJobAssignment();
    }
  };
  const goToJobAssignment = () => {
    setPhase('job_assignment');
    setJobRevealIndex(-1);
  };
  // --- JOB ASSIGNMENT ---
  const revealNextJob = () => {
    const nextIdx = jobRevealIndex + 1;
    if (nextIdx >= players.length) return;
    setJobSpinning(true);
    setTimeout(() => {
      const player = players[nextIdx];
      const pathIdx = player.pathIndex !== null ? player.pathIndex : 0;
      const jobs = getJobsForPath(pathIdx);
      const shuffled = [...jobs].sort(() => Math.random() - 0.5);
      setPlayers(prev => prev.map((p, i) => i === nextIdx ? {
        ...p,
        job: shuffled[0]
      } : p));
      setJobSpinning(false);
      setJobRevealIndex(nextIdx);
    }, 2000);
  };
  const startPlaying = () => {
    setBoardLanded(false);
    setPhase('playing');
    setCurrentPlayerIndex(0);
    setDiceValue(null);
    setShowPopup(false);
    setZoomedTile(null);
  };
  // --- INCOME ---
  const earnIncome = (playerIndex: number): number => {
    const player = players[playerIndex];
    if (!player.job) return 0;
    const job = player.job;
    if (job.id === 'entrepreneur' && player.turnsPlayed < 2) return 0;
    let dailyPay = job.wage * 8;
    if (job.id === 'gig-driver') {
      if (Math.random() < 0.5) dailyPay = Math.floor(dailyPay * 0.5);
    }
    if (job.id === 'tech') dailyPay += 20;
    if (job.id === 'successful-streamer' && player.turnsPlayed > 0 && player.turnsPlayed % 2 === 0) dailyPay += 100;
    return dailyPay;
  };
  // --- JOB EFFECTS ---
  const applyJobPreRollEffect = (playerIndex: number): boolean => {
    const player = players[playerIndex];
    if (!player.job) return false;
    const job = player.job;
    const skipAndNext = (msg: string) => {
      setJobEffectMessage(msg);
      setPlayers(prev => prev.map((p, i) => i === playerIndex ? {
        ...p,
        skipNextTurn: true
      } : p));
      setTimeout(() => {
        setJobEffectMessage(null);
        setCurrentPlayerIndex((playerIndex + 1) % players.length);
      }, 2500);
      return true;
    };
    if (job.id === 'healthcare' && player.turnsPlayed > 0 && player.turnsPlayed % 3 === 0) return skipAndNext(`🧑‍⚕️ ${player.name}: Burnout hit! Skip this turn.`);
    if (job.id === 'corporate' && Math.random() < 0.5) return skipAndNext(`💼 ${player.name}: Forgot to log in! Skip this turn.`);
    if (job.id === 'fast-food' && Math.random() < 0.3) return skipAndNext(`🍔 ${player.name}: Lunch rush meltdown! Skip this turn.`);
    if (job.id === 'warehouse' && Math.random() < 0.3) return skipAndNext(`🛒 ${player.name}: Overworked! Skip this turn.`);
    return false;
  };
  // --- ENTREPRENEUR EVOLUTION ---
  const checkEvolution = (playerIndex: number) => {
    const player = players[playerIndex];
    if (!player.job || player.job.id !== 'entrepreneur' || player.evolved || player.turnsPlayed < 5) return;
    const goodJobs = ALL_JOBS.filter(j => j.id === 'successful-streamer' || j.id === 'tech');
    const newJob = goodJobs[Math.floor(Math.random() * goodJobs.length)];
    setEvolvedJob(newJob);
    setShowEvolution(true);
    setPlayers(prev => prev.map((p, i) => i === playerIndex ? {
      ...p,
      job: newJob,
      evolved: true
    } : p));
  };
  // --- GAMEPLAY ---
  const rollDice = () => {
    if (isRolling || showPopup || showEvolution || jobEffectMessage) return;
    const player = players[currentPlayerIndex];
    const income = earnIncome(currentPlayerIndex);
    if (income > 0) {
      setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
        ...p,
        money: p.money + income,
        turnsPlayed: p.turnsPlayed + 1
      } : p));
      setIncomeFlash(`+$${income}`);
      setTimeout(() => setIncomeFlash(null), 1500);
    } else {
      setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
        ...p,
        turnsPlayed: p.turnsPlayed + 1
      } : p));
    }
    checkEvolution(currentPlayerIndex);
    if (showEvolution) return;
    // Broke Streamer special
    if (player.job?.id === 'broke-streamer') {
      setIsRolling(true);
      setTimeout(() => {
        const streamerRoll = Math.floor(Math.random() * 6) + 1;
        setDiceValue(streamerRoll);
        setIsRolling(false);
        if (streamerRoll <= 3) {
          setJobEffectMessage(`📱 ${player.name}: No viewers! Skip turn 😭`);
          setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
            ...p,
            skipNextTurn: true
          } : p));
          setTimeout(() => {
            setJobEffectMessage(null);
            setCurrentPlayerIndex((currentPlayerIndex + 1) % players.length);
          }, 2500);
        } else {
          setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
            ...p,
            money: p.money + 50
          } : p));
          setJobEffectMessage(`📱 ${player.name}: Someone gifted! +$50 🎉`);
          setTimeout(() => {
            setJobEffectMessage(null);
            doNormalRoll();
          }, 2000);
        }
      }, 1500);
      return;
    }
    // House Cleaner special
    if (player.job?.id === 'house-cleaner' && Math.random() < 0.4) {
      const lucky = Math.random() < 0.5;
      if (lucky) {
        setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
          ...p,
          money: p.money + 20
        } : p));
        setJobEffectMessage(`🧹 ${player.name}: Found a tip! +$20 💰`);
      } else {
        setJobEffectMessage(`🧹 ${player.name}: Found something weird... skip turn 😭`);
        setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
          ...p,
          skipNextTurn: true
        } : p));
        setTimeout(() => {
          setJobEffectMessage(null);
          setCurrentPlayerIndex((currentPlayerIndex + 1) % players.length);
        }, 2500);
        return;
      }
      setTimeout(() => {
        setJobEffectMessage(null);
        doNormalRoll();
      }, 2000);
      return;
    }
    doNormalRoll();
  };
  const doNormalRoll = () => {
    setIsRolling(true);
    setDiceValue(null);
    setTimeout(() => {
      const roll = Math.floor(Math.random() * 6) + 1;
      setDiceValue(roll);
      setIsRolling(false);
      setTimeout(() => movePlayer(roll), 1000);
    }, 1500);
  };
  const movePlayer = (spaces: number) => {
    setPlayers(prev => {
      const np = [...prev];
      let newPos = np[currentPlayerIndex].position + spaces;
      if (newPos >= 29) newPos = 29;
      if (newPos < 0) newPos = 0;
      np[currentPlayerIndex].position = newPos;
      setTimeout(() => handleLandOnTile(newPos, np[currentPlayerIndex]), 1000);
      return np;
    });
  };
  const handleLandOnTile = (tileIndex: number, player: Player) => {
    const tile = TILES[tileIndex];
    if (tile.effect === 'tax') {
      const results = players.map(p => {
        if (!p.job) return {
          name: p.name,
          emoji: '❓',
          taxType: 'N/A',
          taxed: 0,
          remaining: p.money
        };
        const taxAmount = Math.floor(p.money * p.job.taxRate);
        return {
          name: p.name,
          emoji: p.job.emoji,
          taxType: p.job.taxType,
          taxed: taxAmount,
          remaining: p.money - taxAmount
        };
      });
      setTaxResults(results);
      setPlayers(prev => prev.map((p, i) => ({
        ...p,
        money: results[i].remaining
      })));
      setZoomedTile(tileIndex);
      setPhase('tax_event');
      return;
    }
    setZoomedTile(tileIndex);
    setShowPopup(true);
    if (tileIndex === 29) {
      setTimeout(() => {
        setWinner(player);
        setPhase('winner');
      }, 2000);
    }
  };
  const applyEffectAndNextTurn = () => {
    const tile = TILES[zoomedTile || 0];
    let rollAgain = false;
    setPlayers(prev => {
      const np = [...prev];
      const p = np[currentPlayerIndex];
      if (tile.effect === 'move' && tile.effectValue) {
        let pos = p.position + tile.effectValue;
        if (pos >= 29) pos = 29;
        if (pos < 0) pos = 0;
        p.position = pos;
      } else if (tile.effect === 'skip') {
        p.skipNextTurn = true;
      } else if (tile.effect === 'roll_again') {
        rollAgain = true;
      } else if (tile.effect === 'money_loss' && tile.effectValue) {
        p.money = Math.max(0, p.money + tile.effectValue);
      }
      if (p.job?.id === 'retail' && tile.category === 'chaos') p.position = Math.max(0, p.position - 2);
      if (p.job?.id === 'call-center' && tile.category === 'chaos') p.position = Math.max(0, p.position - 1);
      return np;
    });
    setShowPopup(false);
    setZoomedTile(null);
    setDiceValue(null);
    if (!rollAgain) setCurrentPlayerIndex((currentPlayerIndex + 1) % players.length);
  };
  const closeTaxEvent = () => {
    setPhase('playing');
    setTaxResults([]);
    setZoomedTile(null);
    setCurrentPlayerIndex((currentPlayerIndex + 1) % players.length);
  };
  const closeEvolution = () => {
    setShowEvolution(false);
    setEvolvedJob(null);
    doNormalRoll();
  };
  // Handle skip turns
  useEffect(() => {
    if (phase === 'playing' && !showPopup && !isRolling && !jobEffectMessage && !showEvolution) {
      const cp = players[currentPlayerIndex];
      if (cp.skipNextTurn) {
        setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
          ...p,
          skipNextTurn: false
        } : p));
        setTimeout(() => setCurrentPlayerIndex((currentPlayerIndex + 1) % players.length), 1500);
      }
    }
  }, [currentPlayerIndex, phase, showPopup, isRolling, jobEffectMessage, showEvolution]);
  const DiceIcon = diceValue ? [Dice1Icon, Dice2Icon, Dice3Icon, Dice4Icon, Dice5Icon, Dice6Icon][diceValue - 1] : DicesIcon;
  // ===================== RENDER =====================
  return <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-50 to-blue-100 font-sans overflow-hidden relative" data-id="element-1662">
      <FloatingParticles count={40} data-id="element-1663" />

      {/* ========== SETUP ========== */}
      {phase === 'setup' && <div className="absolute inset-0 flex items-center justify-center p-4 z-10" data-id="element-1664">
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 80,
        damping: 20
      }} className="bg-white/70 backdrop-blur-2xl p-8 md:p-12 rounded-[2rem] shadow-[0_20px_80px_rgba(168,85,247,0.15)] border border-white/80 max-w-2xl w-full text-center relative overflow-hidden" data-id="element-1665">
            {/* Decorative glows */}
            <div className="absolute -top-20 -left-20 w-40 h-40 bg-pink-300/30 rounded-full blur-3xl" data-id="element-1666" />
            <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-purple-300/30 rounded-full blur-3xl" data-id="element-1667" />
            <div className="absolute -top-8 -left-8 rotate-12" data-id="element-1668">
              <GlitterHeart size={50} data-id="element-1669" />
            </div>
            <div className="absolute -bottom-8 -right-8 -rotate-12" data-id="element-1670">
              <GlitterHeart size={50} data-id="element-1671" />
            </div>

            <motion.div animate={{
          y: [0, -5, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1672">
              <h1 className="text-5xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 mb-2 pb-1" style={{
            fontFamily: '"Dancing Script", cursive'
          }} data-id="element-1673">
                Trippin' Through Life
              </h1>
            </motion.div>
            <p className="text-purple-400 font-bold mb-2 text-lg" data-id="element-1674">
              The chaotic game of glow-ups, breakdowns & tax season. 💀
            </p>

            {/* 4 Life Paths Preview */}
            <div className="grid grid-cols-2 gap-2 mb-6 mt-4" data-id="element-1675">
              {LIFE_PATHS.map((path, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3 + i * 0.1
          }} className={`${path.bgLight} ${path.borderColor} border rounded-xl p-2 text-center`} data-id="element-1676">
                  <span className="text-lg" data-id="element-1677">{path.emoji}</span>
                  <p className={`text-[10px] font-bold ${path.textColor} leading-tight`} data-id="element-1678">
                    {path.name}
                  </p>
                </motion.div>)}
            </div>

            <div className="space-y-3 mb-6 text-left" data-id="element-1679">
              {players.map((player, index) => <motion.div key={player.id} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.05
          }} className="flex items-center gap-3 bg-white/60 backdrop-blur-sm p-3 rounded-2xl border border-purple-100/50 shadow-sm" data-id="element-1680">
                  <label className="relative cursor-pointer shrink-0 group" data-id="element-1681">
                    <input type="file" accept="image/*" className="hidden" onChange={e => {
                const f = e.target.files?.[0];
                if (f) handleAvatarUpload(player.id, f);
              }} data-id="element-1682" />
                    {player.avatar ? <div className={`w-12 h-12 rounded-full border-[3px] overflow-hidden shadow-lg shadow-purple-200/50 ${PLAYER_BORDER[player.color]}`} data-id="element-1683">
                        <img src={player.avatar} alt="" className="w-full h-full object-cover" data-id="element-1684" />
                      </div> : <div className={`w-12 h-12 rounded-full ${PLAYER_COLORS[player.color]} shadow-lg flex items-center justify-center relative`} data-id="element-1685">
                        <CameraIcon className="w-5 h-5 text-white/80" data-id="element-1686" />
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-white rounded-full flex items-center justify-center shadow" data-id="element-1687">
                          <span className="text-[8px] font-bold text-purple-500" data-id="element-1688">
                            +
                          </span>
                        </div>
                      </div>}
                  </label>
                  <input type="text" value={player.name} onChange={e => updatePlayer(player.id, 'name', e.target.value)} className="flex-1 bg-transparent font-bold text-purple-800 text-lg outline-none min-w-0" placeholder={`Player ${index + 1}`} data-id="element-1689" />
                  <select value={player.color} onChange={e => updatePlayer(player.id, 'color', e.target.value as PlayerColor)} className="bg-white/80 border border-purple-200/50 rounded-xl px-2 py-1.5 text-purple-700 font-bold outline-none text-sm backdrop-blur-sm" data-id="element-1690">
                    {(['pink', 'purple', 'blue', 'teal', 'gold', 'coral'] as PlayerColor[]).map(c => <option key={c} value={c} data-id="element-1691">
                        {c.charAt(0).toUpperCase() + c.slice(1)}
                      </option>)}
                  </select>
                  {players.length > 2 && <button onClick={() => removePlayer(player.id)} className="text-rose-400 hover:text-rose-600 font-bold px-2" data-id="element-1692">
                      ✕
                    </button>}
                </motion.div>)}
            </div>
            <p className="text-purple-400 text-sm font-medium mb-6" data-id="element-1693">
              📸 Tap the circle to upload a profile pic!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1694">
              {players.length < 6 && <button onClick={addPlayer} className="px-6 py-3 rounded-full bg-purple-100/80 text-purple-600 font-bold hover:bg-purple-200/80 transition-colors backdrop-blur-sm" data-id="element-1695">
                  + Add Player
                </button>}
              <motion.button onClick={goToPathChoice} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.98
          }} className="px-10 py-4 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold text-xl shadow-[0_8px_30px_rgba(236,72,153,0.3)] hover:shadow-[0_12px_40px_rgba(236,72,153,0.4)] transition-shadow" data-id="element-1696">
                NEXT: CHOOSE YOUR PATH 🔀
              </motion.button>
            </div>
          </motion.div>
        </div>}

      {/* ========== PATH CHOICE ========== */}
      {phase === 'path_choice' && <div className="absolute inset-0 flex items-center justify-center p-4 z-10" data-id="element-1697">
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 80,
        damping: 20
      }} className="bg-white/70 backdrop-blur-2xl p-8 md:p-12 rounded-[2rem] shadow-[0_20px_80px_rgba(168,85,247,0.15)] border border-white/80 max-w-3xl w-full text-center relative overflow-hidden overflow-y-auto max-h-[95vh]" data-id="element-1698">
            <div className="absolute -top-20 -left-20 w-40 h-40 bg-pink-300/20 rounded-full blur-3xl" data-id="element-1699" />
            <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-purple-300/20 rounded-full blur-3xl" data-id="element-1700" />

            <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500 mb-2" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-1701">
              Choose Your Life Path
            </h2>
            <p className="text-purple-400 font-bold mb-2 text-sm" data-id="element-1702">
              Player {pathChoicePlayerIndex + 1} of {players.length}
            </p>

            {/* Current player indicator */}
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-1703">
              {players[pathChoicePlayerIndex].avatar ? <img src={players[pathChoicePlayerIndex].avatar!} alt="" className={`w-12 h-12 rounded-full object-cover border-3 ${PLAYER_BORDER[players[pathChoicePlayerIndex].color]} shadow-lg`} data-id="element-1704" /> : <div className={`w-12 h-12 rounded-full ${PLAYER_COLORS[players[pathChoicePlayerIndex].color]} flex items-center justify-center text-white font-bold text-lg shadow-lg`} data-id="element-1705">
                  {players[pathChoicePlayerIndex].name.charAt(0)}
                </div>}
              <span className="font-bold text-purple-800 text-xl" data-id="element-1706">
                {players[pathChoicePlayerIndex].name}
              </span>
            </div>

            <p className="text-purple-500 font-medium mb-6 text-sm" data-id="element-1707">
              Your path determines which jobs you can get! Choose wisely... 🔮
            </p>

            {/* Already chosen players */}
            {pathChoicePlayerIndex > 0 && <div className="flex flex-wrap justify-center gap-2 mb-4" data-id="element-1708">
                {players.slice(0, pathChoicePlayerIndex).map(p => <div key={p.id} className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${p.pathIndex !== null ? LIFE_PATHS[p.pathIndex].bgLight : 'bg-gray-100'} ${p.pathIndex !== null ? LIFE_PATHS[p.pathIndex].borderColor : 'border-gray-200'} border`} data-id="element-1709">
                    <span data-id="element-1710">{p.name}</span>
                    {p.pathIndex !== null && <span data-id="element-1711">{LIFE_PATHS[p.pathIndex].emoji}</span>}
                  </div>)}
              </div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-1712">
              {LIFE_PATHS.map((path, i) => {
            const pathJobs = getJobsForPath(i);
            return <motion.button key={i} onClick={() => selectPath(pathChoicePlayerIndex, i)} whileHover={{
              scale: 1.03,
              y: -4
            }} whileTap={{
              scale: 0.98
            }} className={`flex flex-col items-center p-5 rounded-2xl border-2 ${path.borderColor} ${path.bgLight} text-left transition-all shadow-sm hover:shadow-lg relative overflow-hidden group`} data-id="element-1713">
                    <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity" style={{
                background: `linear-gradient(135deg, ${path.glowColor}, transparent)`
              }} data-id="element-1714" />
                    <div className="flex items-center gap-2 mb-2" data-id="element-1715">
                      <path.icon className={`w-8 h-8 ${path.textColor}`} data-id="element-1716" />
                      <span className="text-2xl" data-id="element-1717">{path.emoji}</span>
                    </div>
                    <span className={`font-bold text-lg ${path.textColor} mb-2`} data-id="element-1718">
                      {path.name}
                    </span>
                    <div className="w-full space-y-1" data-id="element-1719">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-1720">
                        Possible Jobs:
                      </p>
                      {pathJobs.map(job => <div key={job.id} className="flex items-center gap-1.5 text-xs" data-id="element-1721">
                          <span data-id="element-1722">{job.emoji}</span>
                          <span className="font-medium text-gray-600" data-id="element-1723">
                            {job.name}
                          </span>
                          <span className="text-gray-400" data-id="element-1724">${job.wage}/hr</span>
                        </div>)}
                    </div>
                  </motion.button>;
          })}
            </div>
          </motion.div>
        </div>}

      {/* ========== JOB ASSIGNMENT ========== */}
      {phase === 'job_assignment' && <div className="absolute inset-0 flex items-center justify-center p-4 z-10" data-id="element-1725">
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 80,
        damping: 20
      }} className="bg-white/75 backdrop-blur-2xl p-8 md:p-10 rounded-[2rem] shadow-[0_20px_80px_rgba(168,85,247,0.15)] border border-white/80 max-w-3xl w-full text-center relative overflow-y-auto max-h-[90vh]" data-id="element-1726">
            <div className="absolute -top-16 -left-16 w-32 h-32 bg-amber-300/20 rounded-full blur-3xl" data-id="element-1727" />
            <div className="absolute -bottom-16 -right-16 w-32 h-32 bg-pink-300/20 rounded-full blur-3xl" data-id="element-1728" />
            <BriefcaseIcon className="w-16 h-16 mx-auto text-purple-400 mb-4" data-id="element-1729" />
            <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-purple-500 mb-2" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-1730">
              Job Fair
            </h2>
            <p className="text-purple-400 font-bold mb-8" data-id="element-1731">
              Draw a job card from your chosen life path... 🃏💀
            </p>

            <div className="space-y-4 mb-8" data-id="element-1732">
              {players.map((player, i) => <motion.div key={player.id} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.1
          }} className={`flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${player.job ? 'bg-white/70 border-purple-200/50 shadow-sm' : 'bg-white/30 border-dashed border-purple-100/50'}`} data-id="element-1733">
                  <div className="flex items-center gap-3 min-w-[140px]" data-id="element-1734">
                    {player.avatar ? <img src={player.avatar} alt="" className={`w-10 h-10 rounded-full object-cover border-2 ${PLAYER_BORDER[player.color]} shadow`} data-id="element-1735" /> : <div className={`w-10 h-10 rounded-full ${PLAYER_COLORS[player.color]} flex items-center justify-center text-white font-bold text-sm shadow`} data-id="element-1736">
                        {player.name.charAt(0)}
                      </div>}
                    <div data-id="element-1737">
                      <span className="font-bold text-purple-800 block" data-id="element-1738">
                        {player.name}
                      </span>
                      {player.pathIndex !== null && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${LIFE_PATHS[player.pathIndex].bgLight} ${LIFE_PATHS[player.pathIndex].textColor}`} data-id="element-1739">
                          {LIFE_PATHS[player.pathIndex].emoji}{' '}
                          {LIFE_PATHS[player.pathIndex].name}
                        </span>}
                    </div>
                  </div>
                  <div className="flex-1 text-left" data-id="element-1740">
                    {player.job ? <div data-id="element-1741">
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-1742">
                          <span className="text-2xl" data-id="element-1743">{player.job.emoji}</span>
                          <span className="font-bold text-gray-800" data-id="element-1744">
                            {player.job.name}
                          </span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${player.job.taxType === 'W2' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-1745">
                            {player.job.taxType}
                          </span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${player.job.tier === 'good' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-1746">
                            {player.job.tier === 'good' ? '✨ Good' : '💀 Bad'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-1747">
                          ${player.job.wage}/hr • Tax:{' '}
                          {Math.round(player.job.taxRate * 100)}%
                        </p>
                        <p className="text-xs text-gray-400 italic mt-1" data-id="element-1748">
                          "{player.job.flavorText}"
                        </p>
                      </div> : <div className="flex items-center gap-2" data-id="element-1749">
                        {jobSpinning && jobRevealIndex + 1 === i ? <motion.div animate={{
                  rotate: 360
                }} transition={{
                  duration: 0.5,
                  repeat: Infinity
                }} className="text-2xl" data-id="element-1750">
                            🎰
                          </motion.div> : <span className="text-gray-400 font-medium" data-id="element-1751">
                            {player.pathIndex !== null ? `Ready to draw from ${LIFE_PATHS[player.pathIndex].emoji} path...` : 'Waiting for assignment...'}
                          </span>}
                      </div>}
                  </div>
                </motion.div>)}
            </div>

            {jobRevealIndex < players.length - 1 ? <motion.button onClick={revealNextJob} disabled={jobSpinning} whileHover={!jobSpinning ? {
          scale: 1.05
        } : {}} whileTap={!jobSpinning ? {
          scale: 0.98
        } : {}} className={`px-8 py-4 rounded-full font-bold text-lg shadow-lg transition-all ${jobSpinning ? 'bg-gray-200 text-gray-400' : 'bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-amber-500/30'}`} data-id="element-1752">
                {jobSpinning ? '🎰 Drawing Card...' : `🃏 Draw ${players[jobRevealIndex + 1]?.name}'s Job Card`}
              </motion.button> : <motion.button onClick={startPlaying} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.98
        }} className="px-10 py-4 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold text-xl shadow-[0_8px_30px_rgba(236,72,153,0.3)]" data-id="element-1753">
                LET'S GO! 🎲
              </motion.button>}
          </motion.div>
        </div>}

      {/* ========== PLAYING ========== */}
      {phase === 'playing' && <div className="absolute inset-0 flex flex-col" data-id="element-1754">
          {/* Header */}
          <div className="p-3 flex justify-between items-center bg-white/50 backdrop-blur-xl border-b border-white/60 z-20 shadow-[0_4px_20px_rgba(168,85,247,0.08)]" data-id="element-1755">
            <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-1756">
              Trippin' Through Life
            </h1>
            <div className="flex items-center gap-2" data-id="element-1757">
              {players[currentPlayerIndex].avatar && <img src={players[currentPlayerIndex].avatar!} alt="" className="w-7 h-7 rounded-full object-cover border-2 border-white shadow" data-id="element-1758" />}
              <div className={`px-3 py-1 rounded-full text-white font-bold text-xs shadow-lg ${PLAYER_COLORS[players[currentPlayerIndex].color]}`} data-id="element-1759">
                {players[currentPlayerIndex].name}
              </div>
              {players[currentPlayerIndex].job && <span className="text-sm" data-id="element-1760">
                  {players[currentPlayerIndex].job!.emoji}
                </span>}
              <span className="font-bold text-emerald-600 text-sm" data-id="element-1761">
                ${players[currentPlayerIndex].money}
              </span>
            </div>
          </div>

          {/* Job Effect Message */}
          <AnimatePresence data-id="element-1762">
            {jobEffectMessage && <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="absolute top-16 left-1/2 -translate-x-1/2 z-40 bg-white/90 backdrop-blur-2xl px-6 py-3 rounded-2xl shadow-[0_8px_30px_rgba(168,85,247,0.15)] border border-purple-200/50 text-center max-w-md" data-id="element-1763">
                <p className="font-bold text-gray-800 text-lg" data-id="element-1764">
                  {jobEffectMessage}
                </p>
              </motion.div>}
          </AnimatePresence>

          {/* Income Flash */}
          <AnimatePresence data-id="element-1765">
            {incomeFlash && <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.8
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -30
        }} className="absolute top-16 right-4 z-40 bg-emerald-500 text-white px-4 py-2 rounded-full font-bold text-lg shadow-[0_4px_20px_rgba(16,185,129,0.4)]" data-id="element-1766">
                {incomeFlash}
              </motion.div>}
          </AnimatePresence>

          {/* Evolution Popup */}
          <AnimatePresence data-id="element-1767">
            {showEvolution && evolvedJob && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4" data-id="element-1768">
                <motion.div initial={{
            scale: 0.5
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 100
          }} className="bg-white/90 backdrop-blur-2xl p-8 rounded-[2rem] shadow-[0_20px_80px_rgba(251,191,36,0.3)] border-4 border-amber-300 max-w-md w-full text-center" data-id="element-1769">
                  <SparklesIcon className="w-16 h-16 mx-auto text-amber-400 mb-4" data-id="element-1770" />
                  <h2 className="text-3xl font-bold text-gray-800 mb-2" data-id="element-1771">
                    🧠 → {evolvedJob.emoji}
                  </h2>
                  <h3 className="text-2xl font-bold text-purple-700 mb-2" data-id="element-1772">
                    Your Hustle Paid Off!
                  </h3>
                  <p className="text-lg font-bold text-gray-700 mb-1" data-id="element-1773">
                    {players[currentPlayerIndex].name} evolved into:
                  </p>
                  <p className="text-3xl font-bold text-emerald-600 mb-2" data-id="element-1774">
                    {evolvedJob.emoji} {evolvedJob.name}
                  </p>
                  <p className="text-gray-500 mb-6" data-id="element-1775">
                    ${evolvedJob.wage}/hr • {evolvedJob.taxType}
                  </p>
                  <motion.button onClick={closeEvolution} whileHover={{
              scale: 1.03
            }} whileTap={{
              scale: 0.98
            }} className="w-full py-3 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold text-lg shadow-lg" data-id="element-1776">
                    LET'S GET THIS MONEY 💰
                  </motion.button>
                </motion.div>
              </motion.div>}
          </AnimatePresence>

          {/* ===== THE BOARD ===== */}
          <div className="flex-1 relative overflow-hidden" ref={boardRef} data-id="element-1777">
            <FloatingParticles count={20} data-id="element-1778" />
            <SparkleBurst active={showBurst} data-id="element-1779" />

            {/* Path Labels */}
            <div className="absolute inset-0 pointer-events-none z-[5]" data-id="element-1780">
              {LIFE_PATHS.map((path, i) => {
            const positions = [{
              x: '8%',
              y: '70%'
            }, {
              x: '68%',
              y: '70%'
            }, {
              x: '8%',
              y: '25%'
            }, {
              x: '68%',
              y: '25%'
            }];
            const pos = positions[i];
            return <motion.div key={i} className={`absolute ${path.bgLight} ${path.borderColor} border rounded-xl px-3 py-1.5 backdrop-blur-sm`} style={{
              left: pos.x,
              top: pos.y
            }} animate={{
              boxShadow: glowingPath === i ? `0 0 25px ${path.glowColor}, 0 0 50px ${path.glowColor}` : '0 0 0px transparent',
              scale: glowingPath === i ? 1.05 : 1
            }} transition={{
              duration: 0.8,
              ease: 'easeInOut'
            }} data-id="element-1781">
                    <div className="flex items-center gap-1.5" data-id="element-1782">
                      <span className="text-sm" data-id="element-1783">{path.emoji}</span>
                      <span className={`text-[10px] md:text-xs font-bold ${path.textColor}`} data-id="element-1784">
                        {path.name}
                      </span>
                    </div>
                  </motion.div>;
          })}
            </div>

            {/* Board with drop-in animation */}
            <motion.div className="w-full h-full relative" initial={{
          y: -600,
          opacity: 0
        }} animate={boardLanded ? {
          y: 0,
          opacity: 1
        } : {
          y: -600,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 60,
          damping: 18,
          mass: 1.2
        }} data-id="element-1785">
              {/* Path connection lines */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none z-[1]" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-1786">
                {LIFE_PATHS.map((path, pathIdx) => {
              const pathTiles = PATH_TILE_ASSIGNMENTS[pathIdx];
              const points = [getBoardPosition(0), ...pathTiles.map(id => getBoardPosition(id)), getBoardPosition(29)];
              const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
              const colors = ['#8b5cf6', '#ec4899', '#f97316', '#14b8a6'];
              return <motion.path key={pathIdx} d={d} fill="none" stroke={colors[pathIdx]} strokeWidth="0.5" strokeOpacity={glowingPath === pathIdx ? 0.6 : 0.15} strokeLinecap="round" strokeDasharray="2 1" animate={{
                strokeOpacity: glowingPath === pathIdx ? 0.6 : 0.15
              }} transition={{
                duration: 0.8
              }} data-id="element-1787" />;
            })}
              </svg>

              {/* Tiles — only render tiles that are on a path, plus start/finish/tax */}
              {TILES.filter(tile => {
            if (tile.id === 0 || tile.id === 29 || tile.id === 10 || tile.id === 20 || tile.id === 28) return true;
            return PATH_TILE_ASSIGNMENTS.some(path => path.includes(tile.id));
          }).map(tile => {
            const pos = getBoardPosition(tile.id);
            const style = CATEGORY_STYLES[tile.category];
            const Icon = style.icon;
            const isStart = tile.id === 0;
            const isFinish = tile.id === 29;
            const isMoneyLoss = tile.category === 'money_loss';
            const size = isStart || isFinish ? 'w-[12%] h-[10%]' : 'w-[8%] h-[7%]';
            // Determine which path this tile belongs to for glow
            let tilePath = -1;
            PATH_TILE_ASSIGNMENTS.forEach((pathTiles, idx) => {
              if (pathTiles.includes(tile.id)) tilePath = idx;
            });
            return <motion.div key={tile.id} className={`absolute ${size} rounded-xl border-2 ${style.border} ${style.bg} flex flex-col items-center justify-center p-0.5 text-center z-[2]`} style={{
              left: `${pos.x - (isStart || isFinish ? 6 : 4)}%`,
              top: `${pos.y - (isStart || isFinish ? 5 : 3.5)}%`
            }} animate={{
              boxShadow: tilePath === glowingPath || isStart || isFinish ? `0 0 15px ${LIFE_PATHS[Math.max(0, tilePath)]?.glowColor || 'rgba(255,255,255,0.5)'}` : isMoneyLoss ? '0 0 10px rgba(239,68,68,0.2)' : '0 2px 8px rgba(0,0,0,0.06)'
            }} transition={{
              duration: 0.8
            }} whileHover={{
              scale: 1.15,
              zIndex: 10
            }} data-id="element-1788">
                    <span className="absolute top-0 left-0.5 text-[5px] font-bold text-black/20" data-id="element-1789">
                      {tile.id + 1}
                    </span>
                    <Icon className={`w-2.5 h-2.5 md:w-4 md:h-4 ${isMoneyLoss ? 'text-red-500 opacity-90' : 'opacity-70'}`} data-id="element-1790" />
                    <p className={`text-[5px] md:text-[7px] font-bold leading-tight line-clamp-1 px-0.5 ${isMoneyLoss ? 'text-red-700' : ''}`} data-id="element-1791">
                      {tile.name}
                    </p>
                    {isMoneyLoss && tile.effectValue && <span className="text-[5px] md:text-[6px] font-bold text-red-600 bg-red-100 rounded-full px-1" data-id="element-1792">
                        -${Math.abs(tile.effectValue)}
                      </span>}
                  </motion.div>;
          })}

              {/* Player Pieces */}
              {players.map((player, pIdx) => {
            const pos = getBoardPosition(player.position);
            const ox = pIdx % 3 * 8 - 8;
            const oy = Math.floor(pIdx / 3) * 8 - 4;
            return <motion.div key={player.id} className={`absolute w-7 h-7 md:w-9 md:h-9 rounded-full border-[3px] ${PLAYER_BORDER[player.color]} flex items-center justify-center text-white font-bold text-xs z-30 overflow-hidden ${!player.avatar ? PLAYER_COLORS[player.color] : 'bg-white'}`} style={{
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))'
            }} initial={false} animate={{
              left: `calc(${pos.x}% + ${ox}px - 14px)`,
              top: `calc(${pos.y}% + ${oy}px - 14px)`,
              rotate: boardLanded ? [0, -3, 3, -2, 0] : 0
            }} transition={{
              type: 'spring',
              stiffness: 50,
              damping: 15
            }} data-id="element-1793">
                    {player.avatar ? <img src={player.avatar} alt="" className="w-full h-full object-cover" data-id="element-1794" /> : player.name.charAt(0).toUpperCase()}
                  </motion.div>;
          })}
            </motion.div>

            {/* Tile Popup */}
            <AnimatePresence data-id="element-1795">
              {showPopup && zoomedTile !== null && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="absolute inset-0 flex items-center justify-center z-50 p-4 bg-black/20 backdrop-blur-sm" data-id="element-1796">
                  <motion.div initial={{
              scale: 0.7,
              y: 30
            }} animate={{
              scale: 1,
              y: 0
            }} exit={{
              scale: 0.7,
              y: 30
            }} transition={{
              type: 'spring',
              stiffness: 100,
              damping: 18
            }} className={`bg-white/90 backdrop-blur-2xl p-8 rounded-[2rem] shadow-[0_20px_80px_rgba(0,0,0,0.1)] border-4 ${CATEGORY_STYLES[TILES[zoomedTile].category].border} max-w-md w-full text-center`} data-id="element-1797">
                    {createElement(CATEGORY_STYLES[TILES[zoomedTile].category].icon, {
                className: `w-16 h-16 mx-auto mb-3 ${TILES[zoomedTile].category === 'money_loss' ? 'text-red-500 opacity-90' : 'opacity-70'}`
              })}
                    <div className={`inline-block px-3 py-1 rounded-full font-bold text-xs uppercase tracking-wider mb-3 ${TILES[zoomedTile].category === 'money_loss' ? 'bg-red-100 text-red-600' : 'bg-black/5 text-black/50'}`} data-id="element-1798">
                      {TILES[zoomedTile].category === 'money_loss' ? '💸 money hit' : TILES[zoomedTile].category}
                    </div>
                    <h2 className={`font-heading text-2xl md:text-3xl font-bold mb-3 ${TILES[zoomedTile].category === 'money_loss' ? 'text-red-700' : 'text-gray-800'}`} data-id="element-1799">
                      {TILES[zoomedTile].name}
                    </h2>
                    <p className="text-lg font-bold text-gray-500 mb-2" data-id="element-1800">
                      {TILES[zoomedTile].actionText}
                    </p>
                    {TILES[zoomedTile].effect === 'money_loss' && TILES[zoomedTile].effectValue && <motion.div initial={{
                scale: 0.5,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1,
                x: [0, -4, 4, -4, 4, 0]
              }} transition={{
                duration: 0.5
              }} className="text-3xl font-bold text-red-600 mb-4 py-2 px-4 bg-red-50 rounded-2xl border-2 border-red-200 inline-block" data-id="element-1801">
                          -${Math.abs(TILES[zoomedTile].effectValue!)}
                        </motion.div>}
                    {TILES[zoomedTile].effect !== 'money_loss' && <div className="mb-4" data-id="element-1802" />}
                    <motion.button onClick={applyEffectAndNextTurn} whileHover={{
                scale: 1.03
              }} whileTap={{
                scale: 0.98
              }} className="w-full py-3 rounded-full bg-gradient-to-r from-gray-800 to-gray-900 text-white font-bold text-lg shadow-lg" data-id="element-1803">
                      {zoomedTile === 29 ? 'Claim Victory!' : 'Next Player'}
                    </motion.button>
                  </motion.div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Bottom Bar */}
          <div className="bg-white/60 backdrop-blur-2xl border-t border-white/70 p-3 md:p-4 z-20 shadow-[0_-10px_40px_rgba(168,85,247,0.06)]" data-id="element-1804">
            <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-1805">
              <div className="flex flex-wrap justify-center gap-2 flex-1" data-id="element-1806">
                {players.map((p, i) => <motion.div key={p.id} animate={{
              scale: i === currentPlayerIndex ? 1.05 : 1,
              opacity: i === currentPlayerIndex ? 1 : 0.5
            }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition-all text-xs ${i === currentPlayerIndex ? 'bg-white/80 shadow-md border border-purple-100/50' : ''}`} data-id="element-1807">
                    {p.avatar ? <img src={p.avatar} alt="" className="w-5 h-5 rounded-full object-cover" data-id="element-1808" /> : <div className={`w-4 h-4 rounded-full ${PLAYER_COLORS[p.color]}`} data-id="element-1809" />}
                    <span className="font-bold text-gray-700" data-id="element-1810">{p.name}</span>
                    {p.job && <span className="text-xs" data-id="element-1811">{p.job.emoji}</span>}
                    <span className="font-bold text-emerald-600" data-id="element-1812">
                      ${p.money}
                    </span>
                    {p.skipNextTurn && <span className="text-[9px] bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded-full font-bold" data-id="element-1813">
                        SKIP
                      </span>}
                  </motion.div>)}
              </div>
              <div className="flex items-center gap-4" data-id="element-1814">
                {/* Dice display */}
                <motion.div className="w-16 h-16 bg-white/80 rounded-2xl shadow-inner border border-purple-100/50 flex items-center justify-center backdrop-blur-sm" animate={{
              boxShadow: isRolling ? '0 0 20px rgba(168,85,247,0.3)' : '0 0 0px transparent'
            }} data-id="element-1815">
                  <AnimatePresence mode="wait" data-id="element-1816">
                    {isRolling ? <motion.div key="r" animate={{
                  rotate: 360,
                  scale: [1, 1.2, 1]
                }} transition={{
                  duration: 0.5,
                  repeat: Infinity
                }} className="text-purple-500" data-id="element-1817">
                        <DicesIcon className="w-10 h-10" data-id="element-1818" />
                      </motion.div> : <motion.div key="d" initial={{
                  scale: 0,
                  rotate: -180
                }} animate={{
                  scale: 1,
                  rotate: 0
                }} transition={{
                  type: 'spring',
                  stiffness: 200
                }} className="text-purple-600" data-id="element-1819">
                        <DiceIcon className="w-12 h-12" data-id="element-1820" />
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>
                <motion.button onClick={rollDice} disabled={isRolling || showPopup || players[currentPlayerIndex].skipNextTurn || !!jobEffectMessage || showEvolution} whileHover={!(isRolling || showPopup || players[currentPlayerIndex].skipNextTurn || jobEffectMessage || showEvolution) ? {
              scale: 1.05
            } : {}} whileTap={!(isRolling || showPopup || players[currentPlayerIndex].skipNextTurn || jobEffectMessage || showEvolution) ? {
              scale: 0.98
            } : {}} className={`px-6 py-4 rounded-full font-bold text-lg transition-all ${isRolling || showPopup || players[currentPlayerIndex].skipNextTurn || jobEffectMessage || showEvolution ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-[0_8px_30px_rgba(236,72,153,0.3)]'}`} data-id="element-1821">
                  {players[currentPlayerIndex].skipNextTurn ? 'SKIPPING...' : isRolling ? 'ROLLING...' : 'ROLL DICE 🎲'}
                </motion.button>
              </div>
            </div>
          </div>
        </div>}

      {/* ========== TAX EVENT ========== */}
      {phase === 'tax_event' && <div className="absolute inset-0 flex items-center justify-center z-50 bg-red-900/70 backdrop-blur-md p-4" data-id="element-1822">
          <motion.div initial={{
        scale: 0.5,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        stiffness: 100
      }} className="bg-white/95 backdrop-blur-2xl p-8 md:p-10 rounded-[2rem] shadow-[0_20px_80px_rgba(220,38,38,0.2)] border-4 border-red-400 max-w-lg w-full text-center" data-id="element-1823">
            <SkullIcon className="w-20 h-20 mx-auto text-red-500 mb-4" data-id="element-1824" />
            <h2 className="text-4xl font-bold text-red-600 mb-2" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-1825">
              💸 TAX SEASON 💸
            </h2>
            <p className="text-red-400 font-bold mb-6" data-id="element-1826">
              The IRS has entered the chat. Nobody is safe. 💀
            </p>
            <div className="space-y-3 mb-8 text-left" data-id="element-1827">
              {taxResults.map((r, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.2
          }} className="flex items-center justify-between bg-red-50 p-3 rounded-xl border border-red-100" data-id="element-1828">
                  <div className="flex items-center gap-2" data-id="element-1829">
                    <span className="text-xl" data-id="element-1830">{r.emoji}</span>
                    <span className="font-bold text-gray-800" data-id="element-1831">{r.name}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.taxType === 'W2' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-1832">
                      {r.taxType}
                    </span>
                  </div>
                  <div className="text-right" data-id="element-1833">
                    <span className="font-bold text-red-600" data-id="element-1834">-${r.taxed}</span>
                    <span className="text-gray-400 text-sm ml-2" data-id="element-1835">
                      → ${r.remaining}
                    </span>
                  </div>
                </motion.div>)}
            </div>
            <motion.button onClick={closeTaxEvent} whileHover={{
          scale: 1.03
        }} whileTap={{
          scale: 0.98
        }} className="w-full py-4 rounded-full bg-red-600 text-white font-bold text-lg hover:bg-red-700 transition-colors shadow-lg" data-id="element-1836">
              Survive & Continue 💀
            </motion.button>
          </motion.div>
        </div>}

      {/* ========== WINNER ========== */}
      {phase === 'winner' && winner && <div className="absolute inset-0 flex items-center justify-center z-50 bg-white/80 backdrop-blur-xl" data-id="element-1837">
          <FloatingParticles count={60} data-id="element-1838" />
          {Array.from({
        length: 60
      }).map((_, i) => <motion.div key={i} className={`absolute w-3 h-3 rounded-full ${['bg-pink-400', 'bg-purple-400', 'bg-blue-400', 'bg-amber-300', 'bg-teal-300'][i % 5]}`} initial={{
        top: '50%',
        left: '50%',
        opacity: 1
      }} animate={{
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        opacity: 0,
        rotate: Math.random() * 360
      }} transition={{
        duration: 2 + Math.random() * 2,
        ease: 'easeOut',
        repeat: Infinity
      }} data-id="element-1839" />)}
          <motion.div initial={{
        scale: 0.5,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        stiffness: 80
      }} className="text-center relative z-10" data-id="element-1840">
            {winner.avatar ? <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden border-4 border-amber-300 shadow-[0_0_50px_rgba(251,191,36,0.4)]" data-id="element-1841">
                <img src={winner.avatar} alt="" className="w-full h-full object-cover" data-id="element-1842" />
              </div> : <TrophyIcon className="w-32 h-32 mx-auto text-amber-400 mb-6 drop-shadow-[0_0_30px_rgba(251,191,36,0.5)]" data-id="element-1843" />}
            <h1 className="text-7xl md:text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 mb-4" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-1844">
              Winner!
            </h1>
            <div className="flex items-center justify-center gap-4 mb-4" data-id="element-1845">
              <div className={`w-8 h-8 rounded-full ${PLAYER_COLORS[winner.color]} shadow-lg`} data-id="element-1846" />
              <p className="text-4xl font-bold text-gray-800" data-id="element-1847">{winner.name}</p>
            </div>
            {winner.job && <div className="mb-4" data-id="element-1848">
                <span className="text-2xl" data-id="element-1849">{winner.job.emoji}</span>
                <span className="font-bold text-gray-600 ml-2" data-id="element-1850">
                  {winner.job.name}
                </span>
              </div>}
            <p className="text-3xl font-bold text-emerald-600 mb-8" data-id="element-1851">
              Final Bank: ${winner.money}
            </p>
            <motion.button onClick={() => {
          setPhase('setup');
          setWinner(null);
          setBoardLanded(false);
          setPlayers(prev => prev.map(p => ({
            ...p,
            pathIndex: null,
            job: null,
            money: 0,
            position: 0,
            turnsPlayed: 0,
            evolved: false,
            skipNextTurn: false,
            roundsWithoutIncome: 0
          })));
        }} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.98
        }} className="px-10 py-4 rounded-full bg-gradient-to-r from-gray-800 to-gray-900 text-white font-bold text-xl shadow-xl" data-id="element-1852">
              Play Again 🔄
            </motion.button>
          </motion.div>
        </div>}
    </div>;
}